import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, brl } from "@/lib/api";
import { StatusBadge, StatusTimeline } from "@/components/StatusBadge";
import { Car, Loader2, CalendarClock, Phone, MessageCircle, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ShopHoursCard } from "@/components/BusinessHours";

function waNumber(phone) {
  const digits = (phone || "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.startsWith("55") ? digits : `55${digits}`;
}

function PromoImage({ promoId, className = "" }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let revoke = null;
    (async () => {
      try {
        const resp = await api.get(`/promotions/${promoId}/image`, { responseType: "blob" });
        const u = URL.createObjectURL(resp.data);
        revoke = u;
        setUrl(u);
      } catch {}
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [promoId]);
  if (!url) {
    return (
      <div className={`flex items-center justify-center bg-zinc-900 border border-zinc-800 ${className}`}>
        <Loader2 className="h-5 w-5 text-zinc-600 animate-spin" />
      </div>
    );
  }
  return <img src={url} alt="Promoção" className={`object-cover ${className}`} />;
}

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promos, setPromos] = useState([]);

  const shop = user?.shop;
  const phoneDigits = (shop?.phone || "").replace(/\D/g, "");
  const waDigits = waNumber(shop?.phone);

  useEffect(() => {
    (async () => {
      try {
        const [o, p] = await Promise.all([api.get("/orders"), api.get("/promotions")]);
        setOrders(o.data);
        setPromos(p.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const upcomingRevisions = orders
    .map((o) => ({ o, nr: o.next_revision }))
    .filter(({ nr }) => nr?.date);

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <div className="text-[11px] font-mono-caps text-zinc-500">Olá, {user?.name || "cliente"}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100">Seus veículos</h1>
        <p className="text-sm text-zinc-400 mt-1">Acompanhe o progresso dos serviços em tempo real.</p>
      </div>

      {shop?.phone && (
        <div
          data-testid="shop-contact-card"
          className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        >
          <div className="min-w-0">
            <div className="text-[11px] font-mono-caps text-zinc-500">Fale com a oficina</div>
            <div className="font-display text-lg font-bold text-zinc-100 truncate" translate="no">
              <span>{shop.name}</span>
            </div>
            <div className="text-sm text-zinc-400" translate="no"><span>{shop.phone}</span></div>
          </div>
          <div className="flex gap-2">
            <a
              data-testid="shop-call-btn"
              href={`tel:${phoneDigits}`}
              className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase text-sm tracking-wide transition-colors"
            >
              <Phone className="h-4 w-4" /> Ligar
            </a>
            <a
              data-testid="shop-whatsapp-btn"
              href={`https://wa.me/${waDigits}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold uppercase text-sm tracking-wide transition-colors"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>
      )}

      <ShopHoursCard shop={shop} />

      {promos.length > 0 && (
        <section data-testid="promos-section" className="space-y-3">
          <div className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-amber-brand" />
            <h2 className="font-display text-sm font-bold uppercase text-amber-brand tracking-wider">
              Promoções da oficina
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {promos.map((p) => (
              <article
                key={p.id}
                data-testid={`promo-item-${p.id}`}
                className="rounded-xl border border-amber-brand/40 bg-amber-brand/5 overflow-hidden hover:border-amber-brand transition-colors"
              >
                {p.has_image ? (
                  <PromoImage promoId={p.id} className="h-40 w-full" />
                ) : (
                  <div className="h-28 w-full flex items-center justify-center bg-gradient-to-br from-amber-900/40 to-zinc-900">
                    <Megaphone className="h-9 w-9 text-amber-brand/70" />
                  </div>
                )}
                <div className="p-4">
                  <p className="text-sm text-zinc-100 whitespace-pre-wrap">{p.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {upcomingRevisions.length > 0 && (
        <div data-testid="upcoming-revisions" className="rounded-xl border border-amber-brand/40 bg-amber-brand/5 p-4" translate="no">
          <div className="flex items-center gap-2 mb-2">
            <CalendarClock className="h-4 w-4 text-amber-brand" />
            <div className="text-sm font-semibold text-amber-200">Lembretes de revisão</div>
          </div>
          <ul className="space-y-1 text-sm text-amber-100/90">
            {upcomingRevisions.map(({ o, nr }) => (
              <li key={o.id}>
                <span className="font-bold text-amber-200">{o.vehicle?.plate}</span>
                <span>
                  {" — próxima revisão em "}
                  {new Date(nr.date + "T00:00:00").toLocaleDateString("pt-BR")}
                  {nr.km ? ` ou aos ${nr.km.toLocaleString("pt-BR")} km` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-green-brand" /></div>
      ) : orders.length === 0 ? (
        <div data-testid="customer-orders-empty" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 p-10 text-center">
          <Car className="h-10 w-10 mx-auto text-zinc-700" />
          <p className="mt-3 text-zinc-400 font-medium">Nenhuma ordem de serviço no momento.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <Link
              key={o.id}
              to={`/cliente/ordens/${o.id}`}
              data-testid={`customer-order-card-${o.id}`}
              className="block rounded-xl border border-zinc-800 bg-zinc-950/70 hover:border-green-brand/50 transition-colors p-5"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="text-[11px] font-mono-caps text-zinc-500 flex items-center gap-2 flex-wrap" translate="no">
                    {o.order_number != null && (
                      <span data-testid={`customer-os-number-${o.id}`} className="inline-flex items-center rounded-full border border-green-brand/40 bg-green-brand/10 text-green-neon px-2 py-0.5 text-[10px] font-bold tracking-wider">
                        OS #{o.order_number}
                      </span>
                    )}
                    <span>
                      {[o.vehicle?.make, o.vehicle?.model, (o.vehicle?.year || o.vehicle?.year_model) ? `· ${[o.vehicle.year, o.vehicle.year_model].filter(Boolean).join("/")}` : null]
                        .filter(Boolean)
                        .join(" ")}
                    </span>
                  </div>
                  <div className="font-display text-2xl font-bold uppercase text-zinc-100">{o.vehicle?.plate}</div>
                </div>
                <StatusBadge status={o.status} />
              </div>
              <p className="text-sm text-zinc-300 mb-3 line-clamp-2">{o.description}</p>
              {o.budget?.decision === "pendente" && (
                <div className="text-xs font-mono-caps text-amber-brand mb-3" translate="no">
                  <span>{`Orçamento aguardando sua resposta — ${brl(o.budget.total)}`}</span>
                </div>
              )}
              <StatusTimeline status={o.status} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
